import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import {
  Feedback,
  FeedbackFilter,
  FeedbackListResponse,
  FeedbackNewCountResponse,
  FeedbackStatsResponse,
  FeedbackUpdateStatusPayload,
} from '@/types'
import { toast } from 'sonner'

const FEEDBACK_QUERY_KEY = 'feedback'

export const useFeedbackList = (filters: FeedbackFilter = {}) => {
  return useQuery<FeedbackListResponse>({
    queryKey: [FEEDBACK_QUERY_KEY, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
      const { data } = await api.get(`/feedback?${params.toString()}`)
      return data
    },
  })
}

export const useFeedbackDetail = (id: string) => {
  return useQuery<Feedback>({
    queryKey: [FEEDBACK_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/feedback/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useFeedbackNewCount = () => {
  return useQuery<FeedbackNewCountResponse>({
    queryKey: [FEEDBACK_QUERY_KEY, 'new-count'],
    queryFn: async () => {
      const { data } = await api.get('/feedback/count')
      return data
    },
    refetchInterval: 60_000,
  })
}

export const useFeedbackStats = () => {
  return useQuery<FeedbackStatsResponse>({
    queryKey: [FEEDBACK_QUERY_KEY, 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/feedback/stats')
      return data
    },
  })
}

export const useUpdateFeedbackStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: FeedbackUpdateStatusPayload }) => {
      const { data } = await api.patch(`/feedback/${id}`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEEDBACK_QUERY_KEY] })
      toast.success('Feedback actualizado correctamente')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al actualizar el feedback')
    },
  })
}
